import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { siteSettingsService } from '../../services/siteSettingsService';

const defaultContent = {
  subtitle: 'Who we are and what we do',
  title: 'ABOUT US',
  content: `We connect event planners with the best verified vendors, making it easy to find, compare, and book services for any occasion. Your perfect event starts here.

We believe in customer satisfaction and capturing lasting memories. Our team is committed to quality, transparency, and helping you create unforgettable experiences.`,
  ctaText: 'Contact Us',
  ctaLink: '/contact',
};

const About = () => {
  const { data: settingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const settings = settingsResponse?.data || {};
  const siteName = settings.site_name || 'Our Platform';
  const heroImage = settings.about_hero_image;
  const subtitle = settings.about_subtitle || defaultContent.subtitle;
  const title = settings.about_title || (siteName ? `ABOUT ${siteName.toUpperCase()}` : defaultContent.title);
  const content = settings.about_content || defaultContent.content;
  const ctaText = settings.about_cta_text || defaultContent.ctaText;
  const ctaLink = settings.about_cta_link || defaultContent.ctaLink;

  const paragraphs = content
    ? content.split(/\n\n+/).filter((p) => p.trim())
    : defaultContent.content.split(/\n\n+/);

  return (
    <div className="min-h-screen -mt-16">
      {/* Full Hero Section - extends behind transparent header */}
      <div
        className="relative min-h-screen flex items-center pt-16"
        style={{
          backgroundImage: heroImage ? `url(${heroImage})` : 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/50" />

        <div className="relative page-container py-16 md:py-24 px-4 sm:px-6 w-full">
          <div className="max-w-2xl">
            <p className="text-sm md:text-base text-gray-300 uppercase tracking-wider mb-3">
              {subtitle}
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 md:mb-8 leading-tight">
              {title}
            </h1>
            <div className="space-y-4 text-base md:text-lg text-gray-200 leading-relaxed">
              {paragraphs.map((para, i) => (
                <p key={i}>{para.trim()}</p>
              ))}
            </div>
            {ctaText && (
              <Link
                to={ctaLink?.startsWith('/') ? ctaLink : `/${ctaLink || 'contact'}`}
                className="inline-flex items-center justify-center mt-8 px-6 py-3 bg-white text-black font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                {ctaText}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
